/**
 * Pure view model for the Members screen: turns `/member/list`'s response
 * plus the viewer's role and id into the three sections, in order, with
 * their counts, their rows, and the Invited section's per-row delete
 * permission. No React import, no SDK import, so this compiles and its tests
 * run regardless of any Preflight gate. The pane only draws what this
 * function returns.
 */
import { poolMarkInitials } from "../pools/poolsListState";
import {
  askedAgoLabel,
  memberMedalLine,
  memberStatusPill,
  roleLabel,
  type typeMemberContestStatus,
  type typeMemberPill,
} from "./memberRowFacts";

export type typeRosterMemberInput = {
  userId: string;
  poolRole: "owner" | "admin" | "member";
  status: "pending" | "active" | "suspended" | "left" | "removed" | "rejected";
  joinedAt?: string | null;
  createdAt: string;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  contestStatus?: typeMemberContestStatus | null;
  livesLeft?: number | null;
  isPlayingNow?: boolean | null;
  medalsThisSeason?: number | null;
  medalsAwardedThisSeason?: number | null;
  trophiesThisSeason?: number | null;
};

export type typeRosterInvitedInput = {
  poolInviteId: string;
  invitedUserId: string;
  invitedDisplayName: string;
  canRevoke: boolean;
  invitedByName: string;
  sentAtLabel: string;
};

export type typeRosterMemberRow = {
  userId: string;
  displayName: string;
  usernameLabel: string | null;
  initials: string;
  avatarUrl: string | null;
  roleLabel: string | null;
  pill: typeMemberPill | null;
  medalLine: string | null;
  isPlayingNow: boolean;
  poolRole: "owner" | "admin" | "member";
  status: "pending" | "active" | "suspended" | "left" | "removed" | "rejected";
  contestStatus: typeMemberContestStatus | null;
};

export type typeRosterRequestRow = {
  userId: string;
  displayName: string;
  usernameLabel: string | null;
  initials: string;
  avatarUrl: string | null;
  askedLabel: string;
};

export type typeRosterInvitedRow = {
  poolInviteId: string;
  displayName: string;
  invitedByLabel: string;
  sentAtLabel: string;
  canCancel: boolean;
};

export type typeMembersRosterModel = {
  showRequests: boolean;
  requestCount: number;
  requests: typeRosterRequestRow[];
  showApproveAll: boolean;
  memberCount: number;
  members: typeRosterMemberRow[];
  showInvited: boolean;
  invitedCount: number;
  invited: typeRosterInvitedRow[];
  showSearch: boolean;
  hasSearchMiss: boolean;
  viewerIsOfficer: boolean;
  // Computed from the same single source as each row's own contestStatus,
  // /member/list's contestStatus field, so this is not a second source for
  // one fact and does not reopen what ISSUE-705 closed. True when any
  // member's contest standing could not be resolved during this outage.
  // ISSUE-706.
  contestDataUnavailable: boolean;
};

export const MEMBER_SEARCH_THRESHOLD = 12;

const ROLE_RANK: Record<"owner" | "admin" | "member", number> = {
  owner: 0,
  admin: 1,
  member: 2,
};

function resolvedDisplayName(displayName?: string | null): string {
  const trimmed = (displayName ?? "").trim();
  return trimmed === "" ? "Member" : trimmed;
}

function resolvedUsernameLabel(username?: string | null): string | null {
  const trimmed = username?.trim();
  return trimmed ? `@${trimmed}` : null;
}

export function buildMembersRosterModel(input: {
  members: readonly typeRosterMemberInput[];
  invited: readonly typeRosterInvitedInput[];
  viewerUserId: string;
  viewerPoolRole: "owner" | "admin" | "member";
  searchQuery: string;
  nowMs: number;
}): typeMembersRosterModel {
  const viewerIsOfficer =
    input.viewerPoolRole === "owner" || input.viewerPoolRole === "admin";

  // Computed over the raw input.members, before the kept filter and before
  // the search filter: an outage is a property of the list, not of the
  // rows that survive a search box. ISSUE-706.
  const contestDataUnavailable = input.members.some(
    (member) => member.contestStatus === "unavailable",
  );

  const kept = input.members.filter(
    (member) =>
      member.status !== "left" &&
      member.status !== "removed" &&
      member.status !== "rejected",
  );

  const pendingMembers = kept
    .filter((member) => member.status === "pending")
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const requestCount = pendingMembers.length;
  const showRequests = viewerIsOfficer && requestCount > 0;
  const showApproveAll = showRequests && requestCount > 1;

  const requests: typeRosterRequestRow[] = pendingMembers.map((member) => {
    const displayName = resolvedDisplayName(member.displayName);
    return {
      userId: member.userId,
      displayName,
      usernameLabel: resolvedUsernameLabel(member.username),
      initials: poolMarkInitials(displayName),
      avatarUrl: member.avatarUrl ?? null,
      askedLabel: askedAgoLabel({
        createdAtIso: member.createdAt,
        nowMs: input.nowMs,
      }),
    };
  });

  const activeMembers = kept
    .filter(
      (member) => member.status === "active" || member.status === "suspended",
    )
    .sort((a, b) => {
      const rankDiff = ROLE_RANK[a.poolRole] - ROLE_RANK[b.poolRole];
      if (rankDiff !== 0) return rankDiff;
      const nameDiff = resolvedDisplayName(a.displayName).localeCompare(
        resolvedDisplayName(b.displayName),
        undefined,
        { sensitivity: "base" },
      );
      if (nameDiff !== 0) return nameDiff;
      return a.userId.localeCompare(b.userId);
    });

  const showSearch = activeMembers.length > MEMBER_SEARCH_THRESHOLD;

  const trimmedQuery = input.searchQuery.trim();
  const normalizedQuery = trimmedQuery.startsWith("@")
    ? trimmedQuery.slice(1)
    : trimmedQuery;
  const lowerQuery = normalizedQuery.toLowerCase();

  const filteredMembers =
    trimmedQuery === ""
      ? activeMembers
      : activeMembers.filter((member) => {
          const name = resolvedDisplayName(member.displayName).toLowerCase();
          const username = (member.username ?? "").toLowerCase();
          return name.includes(lowerQuery) || username.includes(lowerQuery);
        });

  const hasSearchMiss = trimmedQuery !== "" && filteredMembers.length === 0;

  const members: typeRosterMemberRow[] = filteredMembers.map((member) => {
    const displayName = resolvedDisplayName(member.displayName);
    const contestStatus = member.contestStatus ?? null;
    return {
      userId: member.userId,
      displayName,
      usernameLabel: resolvedUsernameLabel(member.username),
      initials: poolMarkInitials(displayName),
      avatarUrl: member.avatarUrl ?? null,
      roleLabel: roleLabel(member.poolRole),
      pill: memberStatusPill({
        membershipStatus: member.status,
        contestStatus,
        livesLeft: member.livesLeft ?? null,
      }),
      medalLine: memberMedalLine({
        medalsThisSeason: member.medalsThisSeason ?? null,
        medalsAwardedThisSeason: member.medalsAwardedThisSeason ?? null,
        trophiesThisSeason: member.trophiesThisSeason ?? null,
      }),
      isPlayingNow: member.isPlayingNow === true,
      poolRole: member.poolRole,
      status: member.status,
      contestStatus,
    };
  });

  const invitedCount = input.invited.length;
  const showInvited = invitedCount > 0;
  const invited: typeRosterInvitedRow[] = input.invited.map((row) => ({
    poolInviteId: row.poolInviteId,
    displayName: row.invitedDisplayName,
    invitedByLabel: `Invited by ${row.invitedByName}`,
    sentAtLabel: row.sentAtLabel,
    canCancel: row.canRevoke,
  }));

  return {
    showRequests,
    requestCount,
    requests,
    showApproveAll,
    memberCount: members.length,
    members,
    showInvited,
    invitedCount,
    invited,
    showSearch,
    hasSearchMiss,
    viewerIsOfficer,
    contestDataUnavailable,
  };
}
